import { useState, useEffect, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Database, Bell, X, Plus, Upload, Trash2, FileSpreadsheet, Layers, MicOff, Volume2, Square, Settings } from "lucide-react";
import axios from "axios";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// ============ REALTIME VOICE HOOK ============
const useRealtimeVoice = (voiceSettings) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [userTranscript, setUserTranscript] = useState('');
  const [assistantTranscript, setAssistantTranscript] = useState('');
  const [isInterrupted, setIsInterrupted] = useState(false);
  const [inputAudioLevel, setInputAudioLevel] = useState(0);
  const [outputAudioLevel, setOutputAudioLevel] = useState(0);
  const [connectionError, setConnectionError] = useState(null);
  
  // Store voice settings ref for use in connect
  const voiceSettingsRef = useRef(voiceSettings);
  useEffect(() => {
    voiceSettingsRef.current = voiceSettings;
  }, [voiceSettings]);
  
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const processorRef = useRef(null);
  const playbackContextRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const analyserRef = useRef(null);
  const wasSpeakingRef = useRef(false);
  
  // Play PCM16 audio with level detection
  const playAudio = useCallback((base64Audio) => {
    try {
      if (!playbackContextRef.current) {
        playbackContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      }
      const ctx = playbackContextRef.current;
      
      // Decode base64
      const binary = atob(base64Audio);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      
      // PCM16 to Float32
      const pcm16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(pcm16.length);
      let maxLevel = 0;
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768.0;
        maxLevel = Math.max(maxLevel, Math.abs(float32[i]));
      }
      
      // Update output audio level
      setOutputAudioLevel(maxLevel);
      
      // Create and queue buffer
      const buffer = ctx.createBuffer(1, float32.length, 24000);
      buffer.copyToChannel(float32, 0);
      audioQueueRef.current.push(buffer);
      
      if (!isPlayingRef.current) playNextBuffer();
    } catch (e) { console.error('Playback error:', e); }
  }, []);
  
  const playNextBuffer = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      setIsSpeaking(false);
      setOutputAudioLevel(0);
      return;
    }
    isPlayingRef.current = true;
    setIsSpeaking(true);
    
    const ctx = playbackContextRef.current;
    const buffer = audioQueueRef.current.shift();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.onended = playNextBuffer;
    source.start();
  }, []);
  
  const stopPlayback = useCallback(() => {
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setIsSpeaking(false);
    setOutputAudioLevel(0);
  }, []);
  
  // Connect WebSocket with voice settings
  const connect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return true;
    
    return new Promise((resolve) => {
      const settings = voiceSettingsRef.current;
      const threshold = settings?.threshold || 0.65;
      const silenceDuration = settings?.silenceDuration || 700;
      console.log(`Connecting to Realtime API with threshold=${threshold}, silence=${silenceDuration}...`);
      const ws = new WebSocket(`${WS_URL}/api/realtime?threshold=${threshold}&silence=${silenceDuration}`);
      wsRef.current = ws;
      
      let sessionReady = false;
      
      ws.onopen = () => {
        console.log('WebSocket connected, waiting for session...');
        setIsConnected(true);
      };
      
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          console.log('Realtime event:', data.type);
          
          // Session events
          if (data.type === 'session.created' || data.type === 'session.updated') {
            console.log('Session ready!');
            sessionReady = true;
            resolve(true);
          }
          // Speech detection events
          else if (data.type === 'input_audio_buffer.speech_started') {
            console.log('Speech detected');
            // Check if this is an interruption (ORION was speaking)
            if (wasSpeakingRef.current) {
              console.log('INTERRUPTION DETECTED!');
              setIsInterrupted(true);
              setTimeout(() => setIsInterrupted(false), 1000);
            }
            setIsListening(true);
            stopPlayback();
            setUserTranscript(''); // Clear for new input
          } else if (data.type === 'input_audio_buffer.speech_stopped') {
            console.log('Speech ended');
            setIsListening(false);
          }
          // User transcription events
          else if (data.type === 'conversation.item.input_audio_transcription.completed' || 
                   data.type === 'input_audio_buffer.transcription.completed') {
            if (data.transcript) {
              console.log('User said:', data.transcript);
              setUserTranscript(data.transcript);
            }
          }
          // Assistant transcript events (streaming)
          else if (data.type === 'response.audio_transcript.delta' || 
                   data.type === 'response.output_audio_transcript.delta') {
            if (data.delta) {
              setAssistantTranscript(prev => prev + data.delta);
            }
          }
          else if (data.type === 'response.audio_transcript.done' || 
                   data.type === 'response.output_audio_transcript.done') {
            // Transcript complete
          }
          // Audio response events (handle both beta and GA event names)
          else if ((data.type === 'response.audio.delta' || data.type === 'response.output_audio.delta') && data.delta) {
            playAudio(data.delta);
          }
          // Response lifecycle events
          else if (data.type === 'response.created') {
            console.log('Response started');
            setIsSpeaking(true);
            wasSpeakingRef.current = true;
            setAssistantTranscript(''); // Clear for new response
          } else if (data.type === 'response.done') {
            console.log('Response complete');
            wasSpeakingRef.current = false;
          }
          // Error handling
          else if (data.type === 'error') {
            console.error('Realtime API error:', data.error);
            setConnectionError(data.error?.message || 'Unknown error from OpenAI');
          }
        } catch (err) { 
          console.error('Parse error:', err); 
        }
      };
      
      ws.onerror = (err) => { 
        console.error('WebSocket error:', err);
        setConnectionError('WebSocket connection failed');
        setIsConnected(false); 
        resolve(false); 
      };
      ws.onclose = (e) => { 
        console.log('WebSocket closed:', e.code, e.reason);
        if (e.code === 4001) {
          setConnectionError('OpenAI API key not configured');
        } else if (e.code === 4002) {
          setConnectionError('Failed to connect to OpenAI');
        } else if (e.reason) {
          setConnectionError(e.reason);
        }
        setIsConnected(false); 
        setIsListening(false); 
        if (!sessionReady) resolve(false);
      };
    });
  }, [playAudio, stopPlayback]);
  
  // Start microphone with ScriptProcessor (more compatible)
  const startMicrophone = useCallback(async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      const connected = await connect();
      if (!connected) return false;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          sampleRate: 24000, 
          channelCount: 1, 
          echoCancellation: true, 
          noiseSuppression: true,
          autoGainControl: true,
          // Prefer speech-optimized settings
          latency: 0,
          suppressLocalAudioPlayback: true
        }
      });
      streamRef.current = stream;
      
      const ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      audioContextRef.current = ctx;
      
      const source = ctx.createMediaStreamSource(stream);
      sourceNodeRef.current = source;
      
      // Create analyser for input level detection
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      source.connect(analyser);
      
      // Use ScriptProcessor for compatibility
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      
      processor.onaudioprocess = (e) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          const input = e.inputBuffer.getChannelData(0);
          
          // Calculate input audio level for UI waveform
          let maxLevel = 0;
          for (let i = 0; i < input.length; i++) {
            maxLevel = Math.max(maxLevel, Math.abs(input[i]));
          }
          setInputAudioLevel(maxLevel);
          
          // Always send continuous audio - OpenAI server_vad needs uninterrupted PCM stream
          // to properly detect speech_started / speech_stopped transitions
          // Float32 to PCM16
          const pcm16 = new Int16Array(input.length);
          for (let i = 0; i < input.length; i++) {
            const s = Math.max(-1, Math.min(1, input[i]));
            pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          
          // Base64 encode
          const bytes = new Uint8Array(pcm16.buffer);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
          const base64 = btoa(binary);
          
          wsRef.current.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: base64 }));
        }
      };
      
      source.connect(processor);
      processor.connect(ctx.destination);
      
      setIsListening(true);
      setConnectionError(null); // Clear any previous errors
      return true;
    } catch (e) {
      console.error('Mic error:', e);
      // Surface microphone errors to the user
      let errorMessage = 'Microphone access failed';
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        errorMessage = 'Microphone permission denied — please allow mic access in your browser settings';
      } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
        errorMessage = 'No microphone found — please connect a microphone';
      } else if (e.name === 'NotReadableError' || e.name === 'TrackStartError') {
        errorMessage = 'Microphone is in use by another application';
      } else if (e.message) {
        errorMessage = `Microphone error: ${e.message}`;
      }
      setConnectionError(errorMessage);
      // Disconnect WebSocket since we can't use voice without mic
      if (wsRef.current) { 
        wsRef.current.close(); 
        wsRef.current = null; 
      }
      setIsConnected(false);
      return false;
    }
  }, [connect]);
  
  const stopMicrophone = useCallback(() => {
    if (processorRef.current) { processorRef.current.disconnect(); processorRef.current = null; }
    if (sourceNodeRef.current) { sourceNodeRef.current.disconnect(); sourceNodeRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (audioContextRef.current) { audioContextRef.current.close(); audioContextRef.current = null; }
    setIsListening(false);
    setInputAudioLevel(0);
  }, []);
  
  const disconnect = useCallback(() => {
    stopMicrophone();
    stopPlayback();
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    setIsConnected(false);
    setUserTranscript('');
    setAssistantTranscript('');
  }, [stopMicrophone, stopPlayback]);
  
  // Clear transcripts helper
  const clearTranscripts = useCallback(() => {
    setUserTranscript('');
    setAssistantTranscript('');
  }, []);
  
  useEffect(() => () => disconnect(), [disconnect]);
  
  return { 
    isConnected, 
    isListening, 
    isSpeaking, 
    userTranscript,
    assistantTranscript,
    isInterrupted,
    inputAudioLevel,
    outputAudioLevel,
    connectionError,
    connect, 
    disconnect, 
    startMicrophone, 
    stopMicrophone, 
    stopPlayback,
    clearTranscripts
  };
};

// ============ HUD CORNER BRACKETS ============
const HUDCorner = ({ position }) => {
  const corners = {
    'top-left': 'top-0 left-0 border-t-2 border-l-2',
    'top-right': 'top-0 right-0 border-t-2 border-r-2',
    'bottom-left': 'bottom-0 left-0 border-b-2 border-l-2',
    'bottom-right': 'bottom-0 right-0 border-b-2 border-r-2',
  };
  
  return (
    <div className={`absolute w-8 h-8 border-[#00f0ff]/40 ${corners[position]}`} />
  );
};

// ============ HUD SCANLINES ============
const Scanlines = () => (
  <div className="absolute inset-0 pointer-events-none z-50 overflow-hidden opacity-[0.03]">
    <div className="absolute inset-0" style={{
      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(0,240,255,0.5) 2px, transparent 3px)',
      backgroundSize: '100% 4px'
    }} />
  </div>
);

// ============ ANIMATED GRID ============
const AnimatedGrid = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    <svg className="w-full h-full opacity-20">
      <defs>
        <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
          <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(0,240,255,0.15)" strokeWidth="0.5"/>
        </pattern>
        <radialGradient id="gridFade" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="white" stopOpacity="1"/>
          <stop offset="100%" stopColor="white" stopOpacity="0"/>
        </radialGradient>
        <mask id="gridMask">
          <rect width="100%" height="100%" fill="url(#gridFade)"/>
        </mask>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" mask="url(#gridMask)"/>
    </svg>
  </div>
);

// ============ FLOATING HUD ELEMENTS ============
const FloatingHUD = () => {
  const elements = [
    { x: 5, y: 25, type: 'data', label: 'SYS.CORE' },
    { x: 92, y: 30, type: 'data', label: 'NET.LINK' },
    { x: 8, y: 70, type: 'arc', size: 40 },
    { x: 88, y: 65, type: 'arc', size: 35 },
    { x: 15, y: 45, type: 'bracket' },
    { x: 82, y: 50, type: 'bracket' },
  ];

  return (
    <div className="absolute inset-0 pointer-events-none z-0">
      {elements.map((el, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ left: `${el.x}%`, top: `${el.y}%` }}
          animate={{ opacity: [0.2, 0.4, 0.2], y: [0, -5, 0] }}
          transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut" }}
        >
          {el.type === 'data' && (
            <div className="font-mono text-[10px] text-[#00f0ff]/30 tracking-widest">
              {el.label}
              <div className="w-16 h-px bg-gradient-to-r from-[#00f0ff]/30 to-transparent mt-1" />
            </div>
          )}
          {el.type === 'arc' && (
            <motion.svg 
              width={el.size} 
              height={el.size} 
              viewBox="0 0 40 40"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <circle cx="20" cy="20" r="18" fill="none" stroke="rgba(0,240,255,0.15)" strokeWidth="1" strokeDasharray="8 4"/>
            </motion.svg>
          )}
          {el.type === 'bracket' && (
            <svg width="30" height="30" viewBox="0 0 30 30" className="opacity-20">
              <path d="M0 10 L0 0 L10 0" fill="none" stroke="#00f0ff" strokeWidth="1"/>
              <path d="M30 20 L30 30 L20 30" fill="none" stroke="#00f0ff" strokeWidth="1"/>
            </svg>
          )}
        </motion.div>
      ))}
      
      {/* Floating particles */}
      {[...Array(40)].map((_, i) => (
        <motion.div
          key={`particle-${i}`}
          className="absolute w-1 h-1 rounded-full bg-[#00f0ff]"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            opacity: Math.random() * 0.3 + 0.1,
            boxShadow: '0 0 4px rgba(0,240,255,0.5)'
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.1, 0.3, 0.1]
          }}
          transition={{
            duration: 5 + Math.random() * 10,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
};

// ============ IRON MAN STYLE ORB ============
const OrionOrb = ({ isListening, isSpeaking, isProcessing, isWakeWordActive, onClick, audioLevel }) => {
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setRotation(prev => prev + 0.5);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const getColor = () => {
    if (isListening) return { main: "#ff003c", glow: "rgba(255,0,60,0.5)" };
    if (isProcessing) return { main: "#ffcc00", glow: "rgba(255,204,0,0.5)" };
    if (isSpeaking) return { main: "#00ff9d", glow: "rgba(0,255,157,0.5)" };
    return { main: "#00f0ff", glow: "rgba(0,240,255,0.5)" };
  };

  const colors = getColor();
  const pulseIntensity = isListening || isSpeaking ? (audioLevel || 0.5) : 0.3;

  return (
    <div 
      className="relative w-64 h-64 md:w-80 md:h-80 cursor-pointer"
      onClick={onClick}
      data-testid="orb-container"
    >
      {/* Outer ambient glow */}
      <motion.div 
        className="absolute -inset-16 rounded-full"
        animate={{ scale: [1, 1.05, 1], opacity: [0.1, 0.15, 0.1] }}
        transition={{ duration: 3, repeat: Infinity }}
        style={{ background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)` }}
      />

      {/* HUD Ring 1 - Outermost with data points */}
      <svg className="absolute inset-0 w-full h-full" style={{ transform: `rotate(${rotation}deg)` }}>
        <circle cx="50%" cy="50%" r="48%" fill="none" stroke={colors.main} strokeWidth="0.5" opacity="0.2"/>
        {[...Array(36)].map((_, i) => (
          <circle
            key={i}
            cx={`${50 + 46 * Math.cos(i * 10 * Math.PI / 180)}%`}
            cy={`${50 + 46 * Math.sin(i * 10 * Math.PI / 180)}%`}
            r="1"
            fill={i % 3 === 0 ? colors.main : 'transparent'}
            opacity="0.4"
          />
        ))}
      </svg>

      {/* HUD Ring 2 - Dashed */}
      <svg className="absolute inset-4 w-[calc(100%-32px)] h-[calc(100%-32px)]" style={{ transform: `rotate(${-rotation * 0.7}deg)` }}>
        <circle cx="50%" cy="50%" r="48%" fill="none" stroke={colors.main} strokeWidth="1" strokeDasharray="15 8" opacity="0.3"/>
      </svg>

      {/* HUD Ring 3 - Solid with gaps */}
      <svg className="absolute inset-10 w-[calc(100%-80px)] h-[calc(100%-80px)]" style={{ transform: `rotate(${rotation * 0.5}deg)` }}>
        <circle cx="50%" cy="50%" r="48%" fill="none" stroke={colors.main} strokeWidth="2" strokeDasharray="40 20" opacity="0.4"/>
      </svg>

      {/* HUD Ring 4 - Inner tech ring */}
      <svg className="absolute inset-16 w-[calc(100%-128px)] h-[calc(100%-128px)]" viewBox="0 0 100 100" style={{ transform: `rotate(${-rotation * 0.3}deg)` }}>
        <circle cx="50" cy="50" r="48" fill="none" stroke={colors.main} strokeWidth="1.5" opacity="0.5"/>
        {[...Array(8)].map((_, i) => {
          const cx = 50 + 42 * Math.cos(i * 45 * Math.PI / 180);
          const cy = 50 + 42 * Math.sin(i * 45 * Math.PI / 180);
          return (
            <rect
              key={i}
              x={cx - 2}
              y={cy - 1}
              width="4"
              height="2"
              fill={colors.main}
              opacity="0.6"
              transform={`rotate(${i * 45}, ${cx}, ${cy})`}
            />
          );
        })}
      </svg>

      {/* Core energy field */}
      <motion.div
        className="absolute inset-20 rounded-full"
        animate={{ scale: [1, 1 + pulseIntensity * 0.1, 1] }}
        transition={{ duration: 0.3, repeat: Infinity }}
        style={{
          background: `radial-gradient(circle at 30% 30%, ${colors.main}50 0%, ${colors.main}20 50%, transparent 70%)`,
          boxShadow: `0 0 60px ${colors.glow}, 0 0 120px ${colors.glow}, inset 0 0 60px ${colors.glow}`,
        }}
      />

      {/* Arc reactor core */}
      <div className="absolute inset-[30%] rounded-full overflow-hidden">
        <motion.div
          className="absolute inset-0 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          style={{
            background: `conic-gradient(from 0deg, transparent, ${colors.main}60, transparent, ${colors.main}60, transparent)`,
          }}
        />
        <div 
          className="absolute inset-2 rounded-full"
          style={{
            background: `radial-gradient(circle, white 0%, ${colors.main} 30%, transparent 70%)`,
            filter: 'blur(2px)'
          }}
        />
      </div>

      {/* Center dot */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white"
        animate={{ scale: [1, 1.2, 1], opacity: [0.8, 1, 0.8] }}
        transition={{ duration: 1, repeat: Infinity }}
        style={{ boxShadow: `0 0 20px white, 0 0 40px ${colors.main}` }}
      />

      {/* Energy particles when active */}
      {(isListening || isSpeaking) && [...Array(16)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            backgroundColor: colors.main,
            top: '50%',
            left: '50%',
            boxShadow: `0 0 6px ${colors.main}`
          }}
          animate={{
            x: [0, Math.cos(i * 22.5 * Math.PI / 180) * 150],
            y: [0, Math.sin(i * 22.5 * Math.PI / 180) * 150],
            opacity: [1, 0],
            scale: [1, 0.2]
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            delay: i * 0.05,
            ease: "easeOut"
          }}
        />
      ))}

      {/* Processing spinner */}
      {isProcessing && (
        <motion.div
          className="absolute inset-8 rounded-full border-2 border-transparent"
          style={{ borderTopColor: colors.main, borderRightColor: colors.main }}
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      )}
    </div>
  );
};

// ============ AUDIO WAVEFORM VISUALIZER ============
const AudioWaveform = ({ inputLevel, outputLevel, isListening, isSpeaking }) => {
  const bars = 32;
  const activeLevel = isSpeaking ? outputLevel : inputLevel;
  const baseColor = isSpeaking ? '#00ff9d' : isListening ? '#ef4444' : '#00f0ff';
  
  return (
    <div className="flex items-center justify-center gap-[2px] h-16 px-4">
      {[...Array(bars)].map((_, i) => {
        // Create symmetric wave pattern
        const centerDistance = Math.abs(i - bars / 2) / (bars / 2);
        const waveHeight = Math.max(0.1, (1 - centerDistance) * activeLevel * 2);
        const randomFactor = 0.7 + Math.random() * 0.6;
        const height = Math.min(1, waveHeight * randomFactor);
        
        return (
          <motion.div
            key={i}
            className="w-1 rounded-full"
            style={{ backgroundColor: baseColor }}
            animate={{ 
              height: `${Math.max(4, height * 48)}px`,
              opacity: 0.4 + height * 0.6
            }}
            transition={{ 
              duration: 0.05,
              ease: "easeOut"
            }}
          />
        );
      })}
    </div>
  );
};

// ============ LIVE TRANSCRIPT PANEL ============
const TranscriptPanel = ({ userTranscript, assistantTranscript, isListening, isSpeaking }) => {
  const [displayedAssistant, setDisplayedAssistant] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  
  // Typing effect for assistant response
  useEffect(() => {
    if (assistantTranscript && assistantTranscript !== displayedAssistant) {
      const timeout = setTimeout(() => {
        setDisplayedAssistant(assistantTranscript.slice(0, displayedAssistant.length + 2));
      }, 20);
      return () => clearTimeout(timeout);
    }
  }, [assistantTranscript, displayedAssistant]);
  
  // Reset displayed text when new response starts
  useEffect(() => {
    if (assistantTranscript === '') {
      setDisplayedAssistant('');
    }
  }, [assistantTranscript]);
  
  // Add to history when response completes
  useEffect(() => {
    if (!isSpeaking && displayedAssistant && displayedAssistant === assistantTranscript) {
      if (userTranscript || assistantTranscript) {
        setConversationHistory(prev => {
          const newHistory = [...prev];
          if (userTranscript) {
            newHistory.push({ role: 'user', content: userTranscript, timestamp: Date.now() });
          }
          if (assistantTranscript) {
            newHistory.push({ role: 'assistant', content: assistantTranscript, timestamp: Date.now() });
          }
          // Keep last 10 messages
          return newHistory.slice(-10);
        });
      }
    }
  }, [isSpeaking, displayedAssistant, assistantTranscript, userTranscript]);
  
  const hasContent = userTranscript || displayedAssistant || conversationHistory.length > 0;
  
  if (!hasContent) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="absolute right-4 top-1/2 -translate-y-1/2 w-80 max-h-[60vh] z-30"
    >
      <div className="bg-black/80 backdrop-blur-md border border-[#00f0ff]/30 rounded-lg overflow-hidden shadow-[0_0_30px_rgba(0,240,255,0.1)]">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#00f0ff]/20 bg-[#00f0ff]/5">
          <div className={`w-2 h-2 rounded-full ${isListening ? 'bg-red-500 animate-pulse' : isSpeaking ? 'bg-green-500 animate-pulse' : 'bg-[#00f0ff]'}`} />
          <span className="font-mono text-[10px] text-[#00f0ff]/80 uppercase tracking-wider">
            {isListening ? 'Listening...' : isSpeaking ? 'Speaking...' : 'Transcript'}
          </span>
        </div>
        
        {/* Conversation history (faded) */}
        {conversationHistory.length > 0 && (
          <div className="max-h-32 overflow-y-auto px-3 py-2 space-y-2 border-b border-[#00f0ff]/10">
            {conversationHistory.slice(-4).map((msg, idx) => (
              <div 
                key={idx} 
                className={`font-mono text-[10px] ${msg.role === 'user' ? 'text-red-400/60' : 'text-[#00ff9d]/60'}`}
                style={{ opacity: 0.4 + (idx / conversationHistory.length) * 0.4 }}
              >
                <span className="text-[#00f0ff]/40 mr-1 text-[9px]">{msg.role === 'user' ? 'YOU' : 'ORION'}</span>
                {msg.content.length > 80 ? msg.content.slice(0, 80) + '...' : msg.content}
              </div>
            ))}
          </div>
        )}
        
        {/* Current transcript */}
        <div className="px-3 py-2 space-y-2 max-h-40 overflow-y-auto">
          {/* User speech (live) */}
          {userTranscript && (
            <motion.div 
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-start gap-2"
            >
              <span className="font-mono text-[10px] text-red-400/80 font-semibold shrink-0">YOU</span>
              <span className="font-mono text-xs text-red-400 break-words">{userTranscript}</span>
            </motion.div>
          )}
          
          {/* Assistant response (typing effect) */}
          {displayedAssistant && (
            <motion.div 
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-start gap-2"
            >
              <span className="font-mono text-[10px] text-[#00ff9d]/80 font-semibold shrink-0">ORION</span>
              <span className="font-mono text-xs text-[#00ff9d] break-words">
                {displayedAssistant}
                {isSpeaking && <span className="animate-pulse">|</span>}
              </span>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ============ DATA SOURCE ACTIVITY INDICATOR ============
const DataSourceActivity = ({ activeSource, isQuerying }) => {
  if (!isQuerying || !activeSource) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="absolute top-8 left-1/2 -translate-x-1/2 z-30"
    >
      <div className="bg-black/80 backdrop-blur-md border border-[#00f0ff]/40 rounded-lg px-4 py-2 flex items-center gap-3">
        {/* Scanning animation */}
        <div className="relative w-8 h-8">
          <motion.div
            className="absolute inset-0 border-2 border-[#00f0ff]/60 rounded-full"
            animate={{ scale: [1, 1.3, 1], opacity: [0.6, 0.2, 0.6] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          <motion.div
            className="absolute inset-1 border border-[#00f0ff] rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            style={{ borderTopColor: 'transparent' }}
          />
          <Database className="absolute inset-0 m-auto w-3 h-3 text-[#00f0ff]" />
        </div>
        
        <div className="flex flex-col">
          <span className="font-mono text-xs text-[#00f0ff]/60 uppercase tracking-wider">Accessing</span>
          <span className="font-mono text-sm text-[#00f0ff] font-semibold">{activeSource}</span>
        </div>
        
        {/* Progress dots */}
        <div className="flex gap-1">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-[#00f0ff]"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

// ============ INTERRUPTION PULSE EFFECT ============
const InterruptionPulse = ({ isInterrupted }) => {
  if (!isInterrupted) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: [0, 1, 0], scale: [0.8, 1.5, 2] }}
      transition={{ duration: 0.8 }}
      className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
    >
      <div className="w-64 h-64 rounded-full border-4 border-red-500/60" 
           style={{ boxShadow: '0 0 60px rgba(239, 68, 68, 0.4), inset 0 0 60px rgba(239, 68, 68, 0.2)' }} />
    </motion.div>
  );
};

// ============ STATUS HUD ============
const StatusHUD = ({ conversationMode, isRealtimeConnected, connectionError }) => (
  <div className="text-center mt-6 w-full max-w-xl mx-auto px-6">
    {/* Show connection error if any */}
    {connectionError && (
      <div className="font-mono text-xs text-red-400 flex items-center justify-center gap-2 mb-2 bg-red-500/10 py-2 px-4 rounded-lg border border-red-500/30">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        {connectionError}
      </div>
    )}
    {/* Show realtime status when connected */}
    {isRealtimeConnected && !connectionError && (
      <div className="font-mono text-xs text-[#00ff9d]/80 flex items-center justify-center gap-2 mb-2">
        <div className="w-2 h-2 rounded-full bg-[#00ff9d] animate-pulse" />
        REALTIME MODE ACTIVE
      </div>
    )}
    {/* Only show tap instruction when NOT in conversation */}
    {!conversationMode && !isRealtimeConnected && !connectionError && (
      <div className="font-mono text-sm text-[#94a3b8]/60">
        Tap to start
      </div>
    )}
    {/* Show listening prompt when in conversation but realtime */}
    {conversationMode && isRealtimeConnected && !connectionError && (
      <div className="font-mono text-xs text-[#00f0ff]/60">
        Speak naturally - I'm listening
      </div>
    )}
  </div>
);

// ============ QUERY HINT ============
const QueryHint = ({ isVisible }) => {
  const [currentHint, setCurrentHint] = useState(0);
  
  const hints = [
    "What brands are we monitoring?",
    "How many calls analyzed today?",
    "Show customer sentiment breakdown",
    "Any flagged calls this week?",
    "What products are tracked?",
    "Show recent complaints",
    "Market trends update",
    "Competitor analysis",
  ];
  
  // Rotate hints every 5 seconds
  useEffect(() => {
    if (!isVisible) return;
    
    const interval = setInterval(() => {
      setCurrentHint(h => (h + 1) % hints.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isVisible, hints.length]);
  
  if (!isVisible) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex items-center justify-center gap-2 mb-4"
      data-testid="query-hint"
    >
      <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0a1520]/80 border border-[#00f0ff]/30">
        <div className="w-2 h-2 rounded-full bg-[#00f0ff] animate-pulse" />
        <motion.span 
          key={currentHint}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-sm font-mono text-[#00f0ff]/80"
        >
          Ask: '{hints[currentHint]}'
        </motion.span>
      </div>
    </motion.div>
  );
};

// ============ SLIDE PANEL ============
const SlidePanel = ({ isOpen, onClose, title, icon: Icon, position = "left", children }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
        />
        
        <motion.div
          initial={{ x: position === "left" ? "-100%" : "100%" }}
          animate={{ x: 0 }}
          exit={{ x: position === "left" ? "-100%" : "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className={`fixed top-0 ${position === "left" ? "left-0" : "right-0"} h-full w-80 
            bg-[#050508]/95 backdrop-blur-xl z-50 flex flex-col
            border-${position === "left" ? "r" : "l"} border-[#00f0ff]/20`}
          data-testid={`panel-${title.toLowerCase().replace(/\s/g, '-')}`}
        >
          {/* Panel HUD corners */}
          <HUDCorner position="top-left" />
          <HUDCorner position="top-right" />
          <HUDCorner position="bottom-left" />
          <HUDCorner position="bottom-right" />

          <div className="flex items-center justify-between p-4 border-b border-[#00f0ff]/10">
            <div className="flex items-center gap-3">
              <Icon className="w-5 h-5 text-[#00f0ff]" />
              <h2 className="font-rajdhani font-bold text-lg tracking-wider uppercase text-[#00f0ff]">
                {title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#00f0ff]/10 border border-transparent hover:border-[#00f0ff]/30 transition-all"
              data-testid="close-panel-btn"
            >
              <X className="w-5 h-5 text-[#94a3b8]" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            {children}
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

// ============ DATA SOURCES CONTENT ============
const DataSourcesContent = ({ dataSources, groupedData, onUploadCSV, onDeleteSource, onDeleteSpreadsheet, onConnectSheets }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showSheetsModal, setShowSheetsModal] = useState(false);
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetName, setSheetName] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.name.endsWith('.csv')) onUploadCSV(file);
  }, [onUploadCSV]);

  const handleConnectSheet = async () => {
    if (!sheetUrl) return;
    setIsConnecting(true);
    try {
      await onConnectSheets(sheetUrl, sheetName);
      setSheetUrl('');
      setSheetName('');
      setShowSheetsModal(false);
    } catch (error) {
      console.error('Failed to connect sheet:', error);
    }
    setIsConnecting(false);
  };

  // Use grouped data if available, otherwise fall back to dataSources
  const csvSources = groupedData?.csv_sources || dataSources.filter(s => s.source_type !== 'google_sheets');
  const spreadsheets = groupedData?.spreadsheets || [];
  const totalSources = csvSources.length + spreadsheets.length;

  return (
    <div className="space-y-6">
      <div>
        <div className="font-mono text-[10px] text-[#00f0ff] uppercase tracking-widest mb-3 flex items-center gap-2">
          <div className="w-1 h-1 bg-[#00f0ff]" />
          CSV FILES
        </div>
        <div
          className={`border border-dashed p-6 text-center cursor-pointer transition-all
            ${isDragging ? 'border-[#00f0ff] bg-[#00f0ff]/10' : 'border-[#00f0ff]/20 hover:border-[#00f0ff]/40'}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid="csv-upload-zone"
        >
          <Upload className="w-8 h-8 mx-auto mb-2 text-[#00f0ff] opacity-50" />
          <div className="font-mono text-xs text-[#e0f7fa]">Drop CSV or click</div>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={(e) => e.target.files?.[0] && onUploadCSV(e.target.files[0])} className="hidden" />
        </div>
      </div>

      <div>
        <div className="font-mono text-[10px] text-[#00f0ff] uppercase tracking-widest mb-3 flex items-center gap-2">
          <div className="w-1 h-1 bg-[#00ff9d]" />
          GOOGLE SHEETS
        </div>
        {!showSheetsModal ? (
          <button onClick={() => setShowSheetsModal(true)} className="w-full flex items-center justify-center gap-2 p-3 border border-[#00f0ff]/20 hover:border-[#00ff9d]/50 hover:bg-[#00ff9d]/5 transition-all font-mono text-xs uppercase" data-testid="connect-sheets-btn">
            <FileSpreadsheet className="w-4 h-4 text-[#00ff9d]" />
            <span className="text-[#e0f7fa]">Connect Sheet</span>
          </button>
        ) : (
          <div className="space-y-3 p-3 border border-[#00ff9d]/30 bg-[#00ff9d]/5">
            <input
              type="text"
              value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)}
              placeholder="Google Sheet URL or ID..."
              className="w-full bg-[#00f0ff]/5 border border-[#00f0ff]/20 text-[#e0f7fa] font-mono text-xs p-2 focus:outline-none focus:border-[#00ff9d]"
              data-testid="sheet-url-input"
            />
            <input
              type="text"
              value={sheetName}
              onChange={(e) => setSheetName(e.target.value)}
              placeholder="Sheet name (optional, defaults to first)"
              className="w-full bg-[#00f0ff]/5 border border-[#00f0ff]/20 text-[#e0f7fa] font-mono text-xs p-2 focus:outline-none focus:border-[#00ff9d]"
              data-testid="sheet-name-input"
            />
            <div className="font-mono text-[10px] text-[#94a3b8]">
              Share sheet with: <span className="text-[#00ff9d]">jarvis@skilful-sphere-490208-j2.iam.gserviceaccount.com</span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowSheetsModal(false)} className="flex-1 p-2 border border-[#ff003c]/30 text-[#ff003c] hover:bg-[#ff003c]/10 font-mono text-xs uppercase">
                Cancel
              </button>
              <button 
                onClick={handleConnectSheet} 
                disabled={isConnecting || !sheetUrl}
                className="flex-1 p-2 border border-[#00ff9d]/30 text-[#00ff9d] hover:bg-[#00ff9d]/10 font-mono text-xs uppercase disabled:opacity-50"
                data-testid="connect-sheet-submit"
              >
                {isConnecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="font-mono text-[10px] text-[#00f0ff] uppercase tracking-widest mb-3 flex items-center gap-2">
          <div className="w-1 h-1 bg-[#ffcc00] animate-pulse" />
          EMERGENT APPS (3)
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-[#ffcc00]/5 border border-[#ffcc00]/20 hover:border-[#ffcc00]/40 transition-all" data-testid="pulse-app">
            <div className="flex items-center gap-2 overflow-hidden">
              <Layers className="w-4 h-4 text-[#ffcc00] shrink-0" />
              <div className="truncate">
                <div className="font-mono text-xs text-[#e0f7fa] truncate">Pulse (CallInsight)</div>
                <div className="font-mono text-[10px] text-[#94a3b8]">1,640 calls • sentiment analysis</div>
              </div>
            </div>
            <div className="w-2 h-2 bg-[#00ff9d] rounded-full animate-pulse" title="Connected" />
          </div>
          <div className="flex items-center justify-between p-3 bg-[#ffcc00]/5 border border-[#ffcc00]/20 hover:border-[#ffcc00]/40 transition-all" data-testid="aura-app">
            <div className="flex items-center gap-2 overflow-hidden">
              <Layers className="w-4 h-4 text-[#ffcc00] shrink-0" />
              <div className="truncate">
                <div className="font-mono text-xs text-[#e0f7fa] truncate">AURA (Review Analytics)</div>
                <div className="font-mono text-[10px] text-[#94a3b8]">product reviews • ratings</div>
              </div>
            </div>
            <div className="w-2 h-2 bg-[#00ff9d] rounded-full animate-pulse" title="Connected" />
          </div>
          <div className="flex items-center justify-between p-3 bg-[#ffcc00]/5 border border-[#ffcc00]/20 hover:border-[#ffcc00]/40 transition-all" data-testid="horizon-app">
            <div className="flex items-center gap-2 overflow-hidden">
              <Layers className="w-4 h-4 text-[#ffcc00] shrink-0" />
              <div className="truncate">
                <div className="font-mono text-xs text-[#e0f7fa] truncate">HORIZON (Market Intel)</div>
                <div className="font-mono text-[10px] text-[#94a3b8]">95 products • 7 brands • web scraping</div>
              </div>
            </div>
            <div className="w-2 h-2 bg-[#00ff9d] rounded-full animate-pulse" title="Connected" />
          </div>
        </div>
      </div>

      {totalSources > 0 && (
        <div>
          <div className="font-mono text-[10px] text-[#00f0ff] uppercase tracking-widest mb-3 flex items-center gap-2">
            <div className="w-1 h-1 bg-[#00f0ff] animate-pulse" />
            CONNECTED ({totalSources})
          </div>
          <div className="space-y-2">
            {/* CSV Sources */}
            {csvSources.map((source) => (
              <div key={source.id} className="flex items-center justify-between p-3 bg-[#00f0ff]/5 border border-[#00f0ff]/10 hover:border-[#00f0ff]/30 transition-all" data-testid={`source-${source.id}`}>
                <div className="flex items-center gap-2 overflow-hidden">
                  <Database className="w-4 h-4 text-[#00f0ff] shrink-0" />
                  <div className="truncate">
                    <div className="font-mono text-xs text-[#e0f7fa] truncate">{source.name}</div>
                    <div className="font-mono text-[10px] text-[#94a3b8]">{source.row_count} rows • csv</div>
                  </div>
                </div>
                <button onClick={() => onDeleteSource(source.id)} className="p-1.5 hover:bg-[#ff003c]/20 transition-colors" data-testid={`delete-source-${source.id}`}>
                  <Trash2 className="w-4 h-4 text-[#ff003c]" />
                </button>
              </div>
            ))}
            {/* Google Spreadsheets (grouped) */}
            {spreadsheets.map((spreadsheet) => (
              <div key={spreadsheet.id} className="flex items-center justify-between p-3 bg-[#00ff9d]/5 border border-[#00ff9d]/10 hover:border-[#00ff9d]/30 transition-all" data-testid={`spreadsheet-${spreadsheet.id}`}>
                <div className="flex items-center gap-2 overflow-hidden">
                  <FileSpreadsheet className="w-4 h-4 text-[#00ff9d] shrink-0" />
                  <div className="truncate">
                    <div className="font-mono text-xs text-[#e0f7fa] truncate">{spreadsheet.name}</div>
                    <div className="font-mono text-[10px] text-[#94a3b8]">
                      {spreadsheet.sheet_count} sheets • {spreadsheet.total_rows.toLocaleString()} rows
                    </div>
                  </div>
                </div>
                <button onClick={() => onDeleteSpreadsheet(spreadsheet.id)} className="p-1.5 hover:bg-[#ff003c]/20 transition-colors" data-testid={`delete-spreadsheet-${spreadsheet.id}`}>
                  <Trash2 className="w-4 h-4 text-[#ff003c]" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ REMINDERS CONTENT ============
const RemindersContent = ({ reminders, onAddReminder, onDeleteReminder }) => {
  const [showForm, setShowForm] = useState(false);
  const [text, setText] = useState('');
  const [time, setTime] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (text && time) {
      onAddReminder(text, new Date(time).toISOString());
      setText(''); setTime(''); setShowForm(false);
    }
  };

  const activeReminders = reminders.filter(r => !r.triggered);

  return (
    <div className="space-y-6">
      {!showForm ? (
        <button onClick={() => setShowForm(true)} className="w-full flex items-center justify-center gap-2 p-3 border border-[#00f0ff]/20 hover:border-[#00f0ff]/50 hover:bg-[#00f0ff]/5 transition-all font-mono text-xs uppercase" data-testid="add-reminder-btn">
          <Plus className="w-4 h-4" />
          <span>Add Reminder</span>
        </button>
      ) : (
        <motion.form initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={handleSubmit} className="space-y-3">
          <input type="text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Reminder text..." className="w-full bg-[#00f0ff]/5 border border-[#00f0ff]/20 text-[#e0f7fa] font-mono text-sm p-3 focus:outline-none focus:border-[#00f0ff]" data-testid="reminder-text-input" />
          <input type="datetime-local" value={time} onChange={(e) => setTime(e.target.value)} className="w-full bg-[#00f0ff]/5 border border-[#00f0ff]/20 text-[#e0f7fa] font-mono text-sm p-3 focus:outline-none focus:border-[#00f0ff]" data-testid="reminder-time-input" />
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 p-2 border border-[#ff003c]/30 text-[#ff003c] hover:bg-[#ff003c]/10 font-mono text-xs uppercase">Cancel</button>
            <button type="submit" className="flex-1 p-2 border border-[#00f0ff]/30 text-[#00f0ff] hover:bg-[#00f0ff]/10 font-mono text-xs uppercase" data-testid="save-reminder-btn">Set</button>
          </div>
        </motion.form>
      )}

      {activeReminders.length > 0 && (
        <div>
          <div className="font-mono text-[10px] text-[#ffcc00] uppercase tracking-widest mb-3 flex items-center gap-2">
            <div className="w-1 h-1 bg-[#ffcc00] animate-pulse" />
            ACTIVE ({activeReminders.length})
          </div>
          <div className="space-y-2">
            {activeReminders.map((reminder) => (
              <div key={reminder.id} className="flex items-start justify-between p-3 bg-[#ffcc00]/5 border border-[#ffcc00]/20" data-testid={`reminder-${reminder.id}`}>
                <div className="flex items-start gap-2 overflow-hidden">
                  <Bell className="w-4 h-4 text-[#ffcc00] shrink-0 mt-0.5" />
                  <div className="overflow-hidden">
                    <div className="font-mono text-xs text-[#e0f7fa]">{reminder.text}</div>
                    <div className="font-mono text-[10px] text-[#94a3b8]">{new Date(reminder.time).toLocaleString()}</div>
                  </div>
                </div>
                <button onClick={() => onDeleteReminder(reminder.id)} className="p-1.5 hover:bg-[#ff003c]/20 transition-colors shrink-0" data-testid={`delete-reminder-${reminder.id}`}>
                  <X className="w-3 h-3 text-[#ff003c]" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {reminders.length === 0 && !showForm && (
        <div className="text-center py-8 text-[#94a3b8] font-mono text-xs">
          No reminders set.<br />
          <span className="text-[#00f0ff]/50">Say "Hey ORION, remind me..."</span>
        </div>
      )}
    </div>
  );
};

// ============ FULL SCREEN REMINDER POPUP ============
const ReminderPopup = ({ reminder, onDismiss }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] flex items-center justify-center"
    data-testid="reminder-popup"
  >
    {/* Backdrop with blur */}
    <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onDismiss} />
    
    {/* Animated border frame */}
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.8, opacity: 0 }}
      transition={{ type: "spring", damping: 20 }}
      className="relative w-full max-w-2xl mx-4"
    >
      {/* Glowing border effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-[#ffcc00] via-[#ff9500] to-[#ffcc00] opacity-50 blur-lg animate-pulse" />
      
      {/* Main content */}
      <div className="relative bg-[#0a0a0f] border-2 border-[#ffcc00] p-8">
        {/* Corner brackets */}
        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#ffcc00]" />
        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#ffcc00]" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#ffcc00]" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#ffcc00]" />

        {/* Alert icon */}
        <div className="flex justify-center mb-6">
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="w-20 h-20 rounded-full bg-[#ffcc00]/20 border-2 border-[#ffcc00] flex items-center justify-center"
          >
            <Bell className="w-10 h-10 text-[#ffcc00]" />
          </motion.div>
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          <div className="font-mono text-xs text-[#ffcc00] tracking-widest mb-2">REMINDER ALERT</div>
          <h2 className="font-rajdhani font-bold text-3xl text-[#e0f7fa] tracking-wider">
            ATTENTION REQUIRED
          </h2>
        </div>

        {/* Reminder text */}
        <div className="bg-[#ffcc00]/5 border border-[#ffcc00]/30 p-6 mb-6">
          <p className="font-mono text-lg text-[#e0f7fa] text-center leading-relaxed">
            {reminder?.text}
          </p>
          <div className="font-mono text-xs text-[#94a3b8] text-center mt-4">
            Scheduled: {reminder?.time ? new Date(reminder.time).toLocaleString() : ''}
          </div>
        </div>

        {/* Dismiss button */}
        <div className="flex justify-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onDismiss}
            className="px-8 py-3 bg-[#ffcc00]/10 border-2 border-[#ffcc00] text-[#ffcc00] font-rajdhani font-bold text-lg tracking-wider hover:bg-[#ffcc00]/20 transition-all"
            data-testid="dismiss-reminder-btn"
          >
            ACKNOWLEDGE
          </motion.button>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

// ============ TOAST (for non-reminder notifications) ============
const Toast = ({ message, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 50, x: '-50%' }}
    animate={{ opacity: 1, y: 0, x: '-50%' }}
    exit={{ opacity: 0, y: 50, x: '-50%' }}
    className="fixed bottom-24 left-1/2 z-50 bg-[#0a0a0f]/95 backdrop-blur-xl border border-[#00f0ff]/40 px-6 py-4 flex items-center gap-4"
    data-testid="toast-notification"
  >
    <Database className="w-5 h-5 text-[#00f0ff]" />
    <span className="font-mono text-sm text-[#e0f7fa]">{message}</span>
    <button onClick={onClose} className="p-1 hover:bg-white/10"><X className="w-4 h-4 text-[#94a3b8]" /></button>
  </motion.div>
);

// ============ VOICE SETTINGS PANEL ============
const VoiceSettingsPanel = ({ isOpen, onClose, settings, onSettingsChange }) => {
  const [showHelp, setShowHelp] = useState(false);
  
  if (!isOpen) return null;
  
  const presets = [
    { 
      id: 'sensitive', 
      name: 'Sensitive', 
      description: 'Responds quickly, best for quiet rooms',
      threshold: 0.45,
      silenceDuration: 500,
      icon: '🎯'
    },
    { 
      id: 'balanced', 
      name: 'Balanced', 
      description: 'Good for most environments',
      threshold: 0.65,
      silenceDuration: 700,
      icon: '⚖️'
    },
    { 
      id: 'quiet_environment', 
      name: 'Noisy Environment', 
      description: 'Filters background noise better',
      threshold: 0.8,
      silenceDuration: 900,
      icon: '🔇'
    }
  ];
  
  const handlePresetChange = (preset) => {
    onSettingsChange({
      sensitivity: preset.id,
      threshold: preset.threshold,
      silenceDuration: preset.silenceDuration
    });
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0f1a] border border-[#00f0ff]/30 rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl"
        data-testid="voice-settings-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#00f0ff]/10 flex items-center justify-center">
              <svg className="w-5 h-5 text-[#00f0ff]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-semibold">Voice Settings</h3>
              <p className="text-[#94a3b8] text-xs">Adjust microphone sensitivity</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setShowHelp(!showHelp)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors group"
              data-testid="voice-settings-help"
              title="How to use"
            >
              <svg className={`w-5 h-5 transition-colors ${showHelp ? 'text-[#00f0ff]' : 'text-[#94a3b8] group-hover:text-[#00f0ff]'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="close-voice-settings"
            >
              <svg className="w-5 h-5 text-[#94a3b8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Help Info Tooltip */}
        <AnimatePresence>
          {showHelp && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4 overflow-hidden"
            >
              <div className="p-4 bg-gradient-to-br from-[#00f0ff]/10 to-[#00f0ff]/5 rounded-lg border border-[#00f0ff]/30">
                <h4 className="text-[#00f0ff] font-medium text-sm mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  How Voice Detection Works
                </h4>
                <div className="space-y-3 text-xs text-[#b8c5d6]">
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00f0ff]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#00f0ff] font-bold text-[10px]">1</span>
                    </div>
                    <p><span className="text-white font-medium">Choose your environment</span> – Select a preset that matches where you're speaking (quiet room, normal, or noisy).</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00f0ff]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#00f0ff] font-bold text-[10px]">2</span>
                    </div>
                    <p><span className="text-white font-medium">Threshold</span> – Higher values ignore quiet sounds (good for noisy places). Lower values pick up softer speech.</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#00f0ff]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#00f0ff] font-bold text-[10px]">3</span>
                    </div>
                    <p><span className="text-white font-medium">Pause duration</span> – How long ORION waits after you stop speaking before responding. Longer = more time to think between sentences.</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#00f0ff]/20 flex items-start gap-2">
                  <svg className="w-4 h-4 text-[#ffcc00] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-xs text-[#94a3b8]">Settings apply when you start a <span className="text-white">new voice session</span>. Tap the microphone button to begin.</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Presets */}
        <div className="space-y-3 mb-6">
          <p className="text-[#94a3b8] text-xs font-mono uppercase tracking-wider mb-3">Select Environment</p>
          {presets.map((preset) => (
            <button
              key={preset.id}
              onClick={() => handlePresetChange(preset)}
              className={`w-full p-4 rounded-lg border transition-all text-left ${
                settings.sensitivity === preset.id
                  ? 'bg-[#00f0ff]/10 border-[#00f0ff]/50'
                  : 'bg-[#0a1520] border-[#1e293b] hover:border-[#00f0ff]/30'
              }`}
              data-testid={`preset-${preset.id}`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{preset.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${settings.sensitivity === preset.id ? 'text-[#00f0ff]' : 'text-white'}`}>
                      {preset.name}
                    </span>
                    {settings.sensitivity === preset.id && (
                      <div className="w-2 h-2 rounded-full bg-[#00f0ff] animate-pulse" />
                    )}
                  </div>
                  <p className="text-xs text-[#94a3b8] mt-1">{preset.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
        
        {/* Advanced Settings */}
        <div className="border-t border-[#1e293b] pt-4">
          <p className="text-[#94a3b8] text-xs font-mono uppercase tracking-wider mb-4">Advanced</p>
          
          {/* Threshold Slider */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm text-[#e0f7fa]">Voice Detection Threshold</label>
              <span className="text-xs font-mono text-[#00f0ff]">{settings.threshold.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0.3"
              max="0.95"
              step="0.05"
              value={settings.threshold}
              onChange={(e) => onSettingsChange({ ...settings, threshold: parseFloat(e.target.value) })}
              className="w-full h-2 bg-[#1e293b] rounded-lg appearance-none cursor-pointer accent-[#00f0ff]"
              data-testid="threshold-slider"
            />
            <div className="flex justify-between text-xs text-[#64748b] mt-1">
              <span>More Sensitive</span>
              <span>Less Sensitive</span>
            </div>
          </div>
          
          {/* Silence Duration */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm text-[#e0f7fa]">Pause Before Response</label>
              <span className="text-xs font-mono text-[#00f0ff]">{settings.silenceDuration}ms</span>
            </div>
            <input
              type="range"
              min="300"
              max="1200"
              step="100"
              value={settings.silenceDuration}
              onChange={(e) => onSettingsChange({ ...settings, silenceDuration: parseInt(e.target.value) })}
              className="w-full h-2 bg-[#1e293b] rounded-lg appearance-none cursor-pointer accent-[#00f0ff]"
              data-testid="silence-slider"
            />
            <div className="flex justify-between text-xs text-[#64748b] mt-1">
              <span>Quick Response</span>
              <span>Wait Longer</span>
            </div>
          </div>
        </div>
        
        {/* Note */}
        <div className="mt-4 p-3 bg-[#00f0ff]/5 rounded-lg border border-[#00f0ff]/20">
          <p className="text-xs text-[#94a3b8]">
            <span className="text-[#00f0ff]">Tip:</span> If ORION responds to background noise, try "Noisy Environment". 
            Changes apply on next voice session.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============ MAIN APP ============
function OrionApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  // Voice settings state - MUST be before useRealtimeVoice hook
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [voiceSettings, setVoiceSettings] = useState({
    sensitivity: 'balanced', // 'sensitive', 'balanced', 'quiet_environment'
    threshold: 0.65,
    silenceDuration: 700
  });
  
  // Realtime voice hook - MUST be before any early returns
  const realtime = useRealtimeVoice(voiceSettings);
  
  // Fallback states for REST API (when Realtime not available)
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversationMode, setConversationMode] = useState(false);
  const [status, setStatus] = useState('standby');
  const [audioLevel, setAudioLevel] = useState(0);
  
  const [dataSources, setDataSources] = useState([]);
  const [groupedData, setGroupedData] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [messages, setMessages] = useState([]);
  
  const [showDataPanel, setShowDataPanel] = useState(false);
  const [showRemindersPanel, setShowRemindersPanel] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeReminderPopup, setActiveReminderPopup] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isWakeWordActive, setIsWakeWordActive] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  
  // Data source activity tracking
  const [activeDataSource, setActiveDataSource] = useState(null);
  const [isQueryingData, setIsQueryingData] = useState(false);
  
  // Refs for fallback REST API
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const isRecordingRef = useRef(false);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const recognitionRef = useRef(null);
  
  // Use Realtime states when connected, including audio levels
  const isListening = realtime.isConnected ? realtime.isListening : (status === 'listening');
  const isSpeaking = realtime.isConnected ? realtime.isSpeaking : (status === 'speaking');
  const inputAudioLevel = realtime.isConnected ? realtime.inputAudioLevel : audioLevel;
  const outputAudioLevel = realtime.isConnected ? realtime.outputAudioLevel : (isSpeaking ? 0.5 : 0);
  
  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
      } catch (error) {
        // Not authenticated, redirect to login
        navigate('/login', { replace: true });
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    // Get user from location state (after login) or check auth
    if (location.state?.user) {
      setUser(location.state.user);
      setIsCheckingAuth(false);
    } else {
      checkAuth();
    }
  }, [navigate, location.state]);
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    setUser(null);
    navigate('/login', { replace: true });
  };

  // Update time
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Initialize wake word detection with improved recognition
  useEffect(() => {
    const initWakeWord = async () => {
      // Request microphone permission first
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
          stream.getTracks().forEach(track => track.stop());
        });
      } catch (e) {
        console.log('Mic permission needed');
        setStatusMessage('Tap the orb to enable voice interface');
        return;
      }

      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        recognition.maxAlternatives = 3;
        
        recognition.onstart = () => {
          setIsWakeWordActive(true);
          if (!isRecordingRef.current && !isSpeaking) {
            setStatusMessage('Voice interface active. Say "Hey ORION" to begin.');
          }
        };
        
        recognition.onresult = (event) => {
          if (isRecordingRef.current || isSpeaking) return;
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript.toLowerCase();
            console.log('Heard:', transcript);
            
            // Check for wake word variations
            if (transcript.includes('hey orion') || 
                transcript.includes('orion') || 
                transcript.includes('hey travis') ||
                transcript.includes('hey jarvas') ||
                transcript.includes('a orion') ||
                transcript.includes('hey j')) {
              console.log('Wake word detected!');
              recognition.stop();
              setTimeout(() => startListening(), 100);
              return;
            }
          }
        };
        
        recognition.onerror = (event) => {
          console.log('Recognition error:', event.error);
          if (event.error === 'not-allowed') {
            setStatusMessage('Microphone access needed. Click orb.');
            setIsWakeWordActive(false);
          }
        };
        
        recognition.onend = () => {
          // Auto-restart if not recording or speaking
          if (!isRecordingRef.current && !isSpeaking) {
            setTimeout(() => {
              try {
                recognition.start();
              } catch (e) {
                console.log('Could not restart recognition');
              }
            }, 100);
          } else {
            setIsWakeWordActive(false);
          }
        };
        
        recognitionRef.current = recognition;
        
        // Start listening for wake word
        try {
          recognition.start();
        } catch (e) {
          console.log('Could not start recognition:', e);
        }
      } else {
        setStatusMessage('Voice not supported. Use tap to speak.');
      }
    };
    
    initWakeWord();
    
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
    };
  }, [isSpeaking]);

  // Initialize data
  useEffect(() => {
    const init = async () => {
      try {
        const [sourcesRes, groupedRes, remindersRes] = await Promise.all([
          axios.get(`${API}/csv`),
          axios.get(`${API}/data-sources/grouped`),
          axios.get(`${API}/reminders`)
        ]);
        setDataSources(sourcesRes.data);
        setGroupedData(groupedRes.data);
        setReminders(remindersRes.data);
        // No intro greeting - silent startup
      } catch (error) {
        console.error('Init error:', error);
      }
    };
    init();
  }, []);

  // Check reminders - show full screen popup
  useEffect(() => {
    const checkReminders = () => {
      const now = Date.now();
      reminders.forEach(async (reminder) => {
        if (!reminder.triggered && new Date(reminder.time).getTime() <= now) {
          try {
            await axios.put(`${API}/reminders/${reminder.id}/trigger`);
            setReminders(prev => prev.map(r => r.id === reminder.id ? { ...r, triggered: true } : r));
            // Show full screen popup instead of toast
            setActiveReminderPopup(reminder);
            await speak(`Attention sir. Reminder: ${reminder.text}`);
          } catch (error) {
            console.error('Reminder trigger error:', error);
          }
        }
      });
    };
    const interval = setInterval(checkReminders, 10000);
    return () => clearInterval(interval);
  }, [reminders]);

  // Speak function
  const speak = async (text) => {
    if (!text) return;
    
    // Stop wake word while speaking
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
    }
    setIsWakeWordActive(false);
    
    try {
      setStatus('speaking');
      
      const response = await axios.post(`${API}/speak`, { text, voice: 'onyx', speed: 0.95 }, { responseType: 'blob' });
      
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        
        // SEAMLESS: Immediately start listening after speaking in conversation mode
        if (conversationMode) {
          setStatus('listening');
          startListening();
        } else {
          setStatus('standby');
        }
      };
      
      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        if (conversationMode) {
          setStatus('listening');
          startListening();
        } else {
          setStatus('standby');
        }
      };
      
      await audio.play();
    } catch (error) {
      console.error('TTS error:', error);
      if (conversationMode) {
        setStatus('listening');
        startListening();
      } else {
        setStatus('standby');
      }
    }
  };

  // Start listening - seamless, no delays
  const startListening = async () => {
    if (isRecordingRef.current) return;
    
    // If speaking, interrupt first
    if (isSpeaking && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
      setStatus('standby');
    }
    
    // Enable conversation mode on first listen
    if (!conversationMode) {
      setConversationMode(true);
    }
    
    // Stop wake word recognition
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
    }
    setIsWakeWordActive(false);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      isRecordingRef.current = true;

      // Audio analysis for silence detection
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);
      analyser.fftSize = 256;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      let silenceStart = null;
      let hasSpoken = false;
      const SILENCE_THRESHOLD = 12;
      const SILENCE_DURATION = 1200; // 1.2 seconds - faster response

      const checkAudio = () => {
        if (!isRecordingRef.current) return;
        
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average / 128);
        
        // Track if user has spoken
        if (average > SILENCE_THRESHOLD + 10) {
          hasSpoken = true;
        }
        
        // Only stop on silence AFTER user has spoken
        if (average < SILENCE_THRESHOLD && hasSpoken) {
          if (!silenceStart) silenceStart = Date.now();
          else if (Date.now() - silenceStart > SILENCE_DURATION) {
            mediaRecorder.stop();
            return;
          }
        } else if (average >= SILENCE_THRESHOLD) {
          silenceStart = null;
        }
        
        requestAnimationFrame(checkAudio);
      };

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        isRecordingRef.current = false;
        stream.getTracks().forEach(track => track.stop());
        if (audioContextRef.current) audioContextRef.current.close();
        setStatus('processing');
        setAudioLevel(0);
        
        if (audioChunksRef.current.length > 0 && hasSpoken) {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          await processAudio(audioBlob);
        } else if (conversationMode) {
          // No speech detected, keep listening
          startListening();
        }
      };

      mediaRecorder.start(100);
      setStatus('listening');
      
      checkAudio();
      
      // Max timeout - extended for natural pauses
      setTimeout(() => {
        if (isRecordingRef.current && mediaRecorder.state === 'recording') {
          mediaRecorder.stop();
        }
      }, 15000);
      
    } catch (error) {
      console.error('Mic error:', error);
      isRecordingRef.current = false;
      setStatus('standby');
      setToast('Microphone access denied');
      
      // Restart wake word
      setTimeout(() => {
        if (recognitionRef.current) {
          try { recognitionRef.current.start(); } catch (e) {}
        }
      }, 500);
    }
  };

  // Toggle voice - use Realtime API when available
  const toggleVoice = async () => {
    // Try Realtime API first (instant voice)
    if (realtime.isConnected || !conversationMode) {
      if (realtime.isSpeaking) {
        realtime.stopPlayback();
        return;
      }
      
      if (realtime.isListening) {
        return; // Let it continue naturally
      }
      
      // Start Realtime conversation
      const started = await realtime.startMicrophone();
      if (started) {
        setConversationMode(true);
        setStatus('listening');
        return;
      }
      // Fall through to REST API if Realtime fails
    }
    
    // Fallback to REST API
    if (isSpeaking) {
      if (audioRef.current) { 
        audioRef.current.pause(); 
        audioRef.current.currentTime = 0;
        audioRef.current = null; 
      }
      startListening();
      return;
    }

    if (isListening || isProcessing) {
      return;
    }

    startListening();
  };

  // End conversation mode
  const endConversation = () => {
    realtime.disconnect();
    setConversationMode(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (isRecordingRef.current) {
      isRecordingRef.current = false;
    }
    setStatus('standby');
  };

  // Process audio - fast and seamless
  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    setStatus('processing');
    
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      
      const transcribeRes = await axios.post(`${API}/transcribe`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      let userText = transcribeRes.data.text;
      userText = userText.replace(/hey orion[,.]?\s*/gi, '').replace(/orion[,.]?\s*/gi, '').trim();
      
      // Check for exit commands
      if (userText.toLowerCase().match(/^(stop|exit|bye|goodbye|end conversation|that's all|thanks bye|good night|see you)$/i)) {
        setIsProcessing(false);
        await speak("Talk to you later!");
        setTimeout(() => endConversation(), 1500);
        return;
      }
      
      if (!userText) {
        setIsProcessing(false);
        if (conversationMode) {
          setStatus('listening');
          startListening();
        } else {
          setStatus('standby');
        }
        return;
      }
      
      setMessages(prev => [...prev, { role: 'user', content: userText }]);
      
      // Detect which data source might be queried based on keywords
      const queryLower = userText.toLowerCase();
      let detectedSource = null;
      if (queryLower.includes('call') || queryLower.includes('pulse') || queryLower.includes('customer')) {
        detectedSource = 'Pulse Analytics';
      } else if (queryLower.includes('review') || queryLower.includes('aura') || queryLower.includes('feedback')) {
        detectedSource = 'AURA Reviews';
      } else if (queryLower.includes('product') || queryLower.includes('brand') || queryLower.includes('horizon') || queryLower.includes('market') || queryLower.includes('competitor') || queryLower.includes('amazon') || queryLower.includes('asin') || queryLower.includes('store') || queryLower.includes('retail')) {
        detectedSource = 'HORIZON Market Intel';
      } else if (queryLower.includes('sales') || queryLower.includes('revenue') || queryLower.includes('data')) {
        detectedSource = 'Sales Data';
      }
      
      if (detectedSource) {
        setActiveDataSource(detectedSource);
        setIsQueryingData(true);
      }
      
      const chatRes = await axios.post(`${API}/chat`, {
        message: userText,
        history: messages.slice(-10),
        data_sources: dataSources.map(s => ({ name: s.name, headers: s.headers, rowCount: s.row_count })),
        reminders: reminders.filter(r => !r.triggered)
      });
      
      // Clear data source indicator
      setIsQueryingData(false);
      setActiveDataSource(null);
      
      const response = chatRes.data.response;
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
      
      if (chatRes.data.action?.action === 'set_reminder') {
        await handleSetReminder(chatRes.data.action.text, chatRes.data.action.isoTime);
      }
      
      setIsProcessing(false);
      await speak(response);
      
    } catch (error) {
      console.error('Processing error:', error);
      setIsQueryingData(false);
      setActiveDataSource(null);
      setIsProcessing(false);
      if (conversationMode) {
        setStatus('listening');
        startListening();
      } else {
        setStatus('standby');
      }
    }
  };

  // Handlers
  const handleCsvUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/csv/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDataSources(prev => [...prev, response.data]);
      setToast(`Connected: ${file.name}`);
      await speak(`Data source connected, sir. ${file.name} with ${response.data.row_count} records.`);
    } catch (error) {
      setToast('Failed to upload CSV');
    }
  };

  const handleDeleteSource = async (sourceId) => {
    try {
      await axios.delete(`${API}/csv/${sourceId}`);
      setDataSources(prev => prev.filter(s => s.id !== sourceId));
      // Refresh grouped data
      const groupedRes = await axios.get(`${API}/data-sources/grouped`);
      setGroupedData(groupedRes.data);
    } catch (error) {}
  };

  const handleDeleteSpreadsheet = async (spreadsheetId) => {
    try {
      await axios.delete(`${API}/spreadsheet/${spreadsheetId}`);
      // Refresh both data lists
      const [sourcesRes, groupedRes] = await Promise.all([
        axios.get(`${API}/csv`),
        axios.get(`${API}/data-sources/grouped`)
      ]);
      setDataSources(sourcesRes.data);
      setGroupedData(groupedRes.data);
      setToast('Spreadsheet removed');
    } catch (error) {
      setToast('Failed to remove spreadsheet');
    }
  };

  const handleConnectSheets = async (sheetUrl, sheetName) => {
    try {
      const response = await axios.post(`${API}/sheets/connect`, { 
        sheet_url: sheetUrl, 
        sheet_name: sheetName || null,
        connect_all: true  // Connect all worksheets
      });
      
      // Handle multiple sheets connected
      if (response.data.connected && Array.isArray(response.data.connected)) {
        // Fetch updated data sources list
        const [sourcesRes, groupedRes] = await Promise.all([
          axios.get(`${API}/csv`),
          axios.get(`${API}/data-sources/grouped`)
        ]);
        setDataSources(sourcesRes.data);
        setGroupedData(groupedRes.data);
        
        const count = response.data.total_connected;
        const totalRows = response.data.connected.reduce((sum, s) => sum + s.row_count, 0);
        setToast(`Connected ${count} sheet${count > 1 ? 's' : ''} from ${response.data.spreadsheet}`);
        await speak(`${count} worksheet${count > 1 ? 's' : ''} connected from ${response.data.spreadsheet}, Ripunjay. Total ${totalRows} records loaded.`);
      } else {
        // Single sheet response (backward compatibility)
        setDataSources(prev => [...prev, response.data]);
        setToast(`Connected: ${response.data.name}`);
        await speak(`Google Sheet connected, Ripunjay. ${response.data.name} with ${response.data.row_count} records loaded.`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to connect Google Sheet';
      setToast(errorMsg);
      throw error;
    }
  };

  const handleSetReminder = async (text, isoTime) => {
    try {
      const response = await axios.post(`${API}/reminders`, { text, time: isoTime });
      setReminders(prev => [...prev, response.data]);
    } catch (error) {}
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await axios.delete(`${API}/reminders/${reminderId}`);
      setReminders(prev => prev.filter(r => r.id !== reminderId));
    } catch (error) {}
  };

  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#00f0ff]/30 border-t-[#00f0ff] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-[#030305] overflow-hidden relative" data-testid="orion-app">
      {/* Background effects */}
      <AnimatedGrid />
      <FloatingHUD />
      <Scanlines />

      {/* Main HUD frame */}
      <div className="absolute inset-4 border border-[#00f0ff]/10 pointer-events-none z-0">
        <HUDCorner position="top-left" />
        <HUDCorner position="top-right" />
        <HUDCorner position="bottom-left" />
        <HUDCorner position="bottom-right" />
      </div>

      {/* Central content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pt-24 pb-36">
        {/* Interruption pulse effect */}
        <InterruptionPulse isInterrupted={realtime.isInterrupted} />
        
        {/* Data source activity indicator */}
        <AnimatePresence>
          <DataSourceActivity activeSource={activeDataSource} isQuerying={isQueryingData} />
        </AnimatePresence>
        
        <OrionOrb 
          isListening={isListening} 
          isSpeaking={isSpeaking} 
          isProcessing={isProcessing}
          isWakeWordActive={isWakeWordActive}
          onClick={toggleVoice}
          audioLevel={audioLevel}
        />
        
        {/* Audio waveform visualizer */}
        {(realtime.isConnected && (isListening || isSpeaking)) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="mt-4"
          >
            <AudioWaveform 
              inputLevel={inputAudioLevel}
              outputLevel={outputAudioLevel}
              isListening={isListening}
              isSpeaking={isSpeaking}
            />
          </motion.div>
        )}
        
        <StatusHUD conversationMode={conversationMode} isRealtimeConnected={realtime.isConnected} connectionError={realtime.connectionError} />
        
        {/* Live transcript panel */}
        {realtime.isConnected && (
          <TranscriptPanel 
            userTranscript={realtime.userTranscript}
            assistantTranscript={realtime.assistantTranscript}
            isListening={isListening}
            isSpeaking={isSpeaking}
          />
        )}
      </div>

      {/* Title */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 text-center pointer-events-none z-20">
        <div className="flex items-center justify-center gap-3 mb-1">
          <div className="w-12 h-px bg-gradient-to-r from-transparent to-[#00f0ff]/50" />
          <h1 className="font-rajdhani font-bold text-3xl md:text-4xl tracking-[0.4em] text-[#00f0ff] drop-shadow-[0_0_30px_rgba(0,240,255,0.5)]">
            O.R.I.O.N
          </h1>
          <div className="w-12 h-px bg-gradient-to-l from-transparent to-[#00f0ff]/50" />
        </div>
        <p className="font-mono text-[10px] text-[#94a3b8] tracking-[0.3em]">
          OPERATIONAL REACTIVE INTELLIGENCE NETWORK
        </p>
      </div>

      {/* Query Hint - above microphone */}
      <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20">
        <QueryHint isVisible={!conversationMode && !realtime.isConnected && !isListening} />
      </div>

      {/* Bottom navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-6 z-20">
        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} onClick={() => setShowDataPanel(true)} className="w-14 h-14 border border-[#00f0ff]/30 bg-[#00f0ff]/5 backdrop-blur flex items-center justify-center hover:border-[#00f0ff] hover:bg-[#00f0ff]/10 transition-all" data-testid="data-panel-btn">
          <Database className="w-5 h-5 text-[#00f0ff]" />
        </motion.button>
        
        {/* Main voice button */}
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={toggleVoice} className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${isListening ? 'bg-[#ff003c]/20 border-2 border-[#ff003c] shadow-[0_0_40px_rgba(255,0,60,0.4)]' : isSpeaking ? 'bg-[#00ff9d]/20 border-2 border-[#00ff9d] shadow-[0_0_40px_rgba(0,255,157,0.4)]' : 'bg-[#00f0ff]/10 border-2 border-[#00f0ff]/50 hover:border-[#00f0ff] hover:shadow-[0_0_40px_rgba(0,240,255,0.3)]'}`} data-testid="voice-btn">
          {isListening ? <MicOff className="w-8 h-8 text-[#ff003c]" /> : isSpeaking ? <Volume2 className="w-8 h-8 text-[#00ff9d]" /> : <Mic className="w-8 h-8 text-[#00f0ff]" />}
        </motion.button>
        
        {/* Stop button - appears when conversation is active */}
        {(realtime.isConnected || conversationMode) && (
          <motion.button 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.1 }} 
            whileTap={{ scale: 0.95 }} 
            onClick={() => {
              // Stop everything
              realtime.disconnect();
              endConversation();
              setToast('Conversation ended');
            }} 
            className="w-14 h-14 border border-red-500/50 bg-red-500/10 backdrop-blur flex items-center justify-center hover:border-red-500 hover:bg-red-500/20 transition-all rounded-lg" 
            data-testid="stop-btn"
          >
            <Square className="w-5 h-5 text-red-500 fill-red-500" />
          </motion.button>
        )}
        
        {/* Reminders button - only show when not in conversation or move it */}
        {!(realtime.isConnected || conversationMode) && (
          <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} onClick={() => setShowRemindersPanel(true)} className="w-14 h-14 border border-[#00f0ff]/30 bg-[#00f0ff]/5 backdrop-blur flex items-center justify-center hover:border-[#00f0ff] hover:bg-[#00f0ff]/10 transition-all relative" data-testid="reminders-panel-btn">
            <Bell className="w-5 h-5 text-[#00f0ff]" />
            {reminders.filter(r => !r.triggered).length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#ffcc00] flex items-center justify-center font-mono text-[10px] text-black font-bold">
                {reminders.filter(r => !r.triggered).length}
              </span>
            )}
          </motion.button>
        )}
      </div>
      
      {/* Reminders button - floating when in conversation */}
      {(realtime.isConnected || conversationMode) && (
        <motion.button 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-8 right-8 w-12 h-12 border border-[#00f0ff]/30 bg-black/50 backdrop-blur flex items-center justify-center hover:border-[#00f0ff] hover:bg-[#00f0ff]/10 transition-all relative z-20 rounded-lg" 
          onClick={() => setShowRemindersPanel(true)}
          data-testid="reminders-floating-btn"
        >
          <Bell className="w-4 h-4 text-[#00f0ff]" />
          {reminders.filter(r => !r.triggered).length > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#ffcc00] flex items-center justify-center font-mono text-[8px] text-black font-bold rounded-full">
              {reminders.filter(r => !r.triggered).length}
            </span>
          )}
        </motion.button>
      )}

      {/* Top left info */}
      <div className="absolute top-4 left-4 font-mono text-[10px] z-20">
        <div className="text-[#94a3b8]/60 mb-1">SYS.STATUS</div>
        <div className="flex items-center gap-2 text-[#00ff9d]">
          <div className="w-1.5 h-1.5 bg-[#00ff9d] shadow-[0_0_8px_#00ff9d]" />
          ONLINE
        </div>
        {user && (
          <div className="mt-2 flex items-center gap-2">
            {user.picture && (
              <img src={user.picture} alt="" className="w-6 h-6 rounded-full border border-[#00f0ff]/30" />
            )}
            <div>
              <div className="text-[#00f0ff]/80 text-[9px]">{user.name}</div>
              <button 
                onClick={handleLogout}
                className="text-[#94a3b8]/40 hover:text-red-400 text-[8px] transition-colors"
              >
                LOGOUT
              </button>
            </div>
          </div>
        )}
        {/* Voice Settings Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowVoiceSettings(true)}
          className="mt-3 flex items-center gap-2 px-2 py-1.5 border border-[#00f0ff]/30 hover:border-[#00f0ff]/60 bg-[#00f0ff]/5 hover:bg-[#00f0ff]/10 rounded transition-all"
          data-testid="voice-settings-btn"
        >
          <Settings className="w-3.5 h-3.5 text-[#00f0ff]" />
          <span className="text-[#00f0ff]/80 text-[9px] uppercase tracking-wider">Voice Settings</span>
        </motion.button>
      </div>
      
      {/* Top right time */}
      <div className="absolute top-4 right-4 text-right font-mono z-20">
        <div className="text-xl text-[#00f0ff] tracking-wider">
          {currentTime.toLocaleTimeString('en-US', { hour12: false })}
        </div>
        <div className="text-[10px] text-[#94a3b8]/60">
          {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()}
        </div>
      </div>

      {/* Panels */}
      <SlidePanel isOpen={showDataPanel} onClose={() => setShowDataPanel(false)} title="Data Sources" icon={Database} position="left">
        <DataSourcesContent 
          dataSources={dataSources} 
          groupedData={groupedData}
          onUploadCSV={handleCsvUpload} 
          onDeleteSource={handleDeleteSource} 
          onDeleteSpreadsheet={handleDeleteSpreadsheet}
          onConnectSheets={handleConnectSheets} 
        />
      </SlidePanel>

      <SlidePanel isOpen={showRemindersPanel} onClose={() => setShowRemindersPanel(false)} title="Reminders" icon={Bell} position="right">
        <RemindersContent reminders={reminders} onAddReminder={handleSetReminder} onDeleteReminder={handleDeleteReminder} />
      </SlidePanel>

      {/* Full Screen Reminder Popup */}
      <AnimatePresence>
        {activeReminderPopup && (
          <ReminderPopup 
            reminder={activeReminderPopup} 
            onDismiss={() => setActiveReminderPopup(null)} 
          />
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && <Toast message={toast} onClose={() => setToast(null)} />}
      </AnimatePresence>

      {/* Voice Settings Panel */}
      <VoiceSettingsPanel 
        isOpen={showVoiceSettings}
        onClose={() => setShowVoiceSettings(false)}
        settings={voiceSettings}
        onSettingsChange={(newSettings) => {
          setVoiceSettings(newSettings);
          setToast(`Voice settings updated: ${newSettings.sensitivity}`);
        }}
      />
    </div>
  );
}

// Main App with routing
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/" element={<OrionApp />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
